import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'multireducer';
import bi from 'shared/bi/bi';

import WixSDK from 'shared/utils/wix-sdk';
import decorate from 'widget/utils/decorate';

import WindowSize from 'shared/containers/window-size';
import LocaleProvider from 'shared/containers/locale-provider';

import HomeView from 'mobile/views/home/home';
import TooltipPopout from 'shared/components/tooltip/tooltip-popout';
import { MobileModal } from 'shared/components/modal/modal';
import BIUnhandledErrorLogger from 'shared/bi/bi-unhandled-error-logger';
import MobileSpinnerOverlay from 'mobile/components/mobile-spinner-overlay/mobile-spinner-overlay';
import WidgetHeightByContentSync from './widget-height-by-content-sync';

import { selectVideo } from 'widget/redux/client/actions/select-video';
import { getVideosGroupedByIds } from 'shared/selectors/videos';
import { fetchAppSettings } from 'widget/redux/client/actions/app-settings';
import fetchCurrentSiteUser from 'widget/redux/server/actions/current-site-user/get';
import getPaymentInfo from 'widget/redux/server/actions/channel/payment';
import { getPublic as getChannel } from 'widget/redux/server/actions/channel/get-public';
import { loadFirstPage, addToList } from 'widget/redux/client/actions/channel-videos-lazy-load';
import { getPublic as getVideo } from 'widget/redux/server/actions/channel/video/get';
import { getMainVideoId } from '@wix/wix-vod-shared/src/widget/ui-selectors/selected-video-id';

import BiHandler from 'shared/bi/bi-handler';
import UpdateChannelPublishedState from 'widget/data-components/update-channel-published-state';
import ReloadChannelPaymentDataOnPayment from 'widget/data-components/reload-channel-payment-data-on-payment';
import ClearVideoUrlsCacheOnPayment from 'widget/data-components/clear-video-urls-cache-on-payment';
import PaymentEventProxy from 'shared/components/payment-events/proxy';
import PaymentsBiHandler from 'widget/components/payments/bi-handler';

import { getEditMode } from 'shared/selectors/app-settings';
import { getVideoIdFromUrl } from 'widget/selectors/query-params.js';

import { MOBILE_WIDGET_DATA_KEY } from 'widget/data-components/lazy-channel-videos';
import { VIDEOS_COUNT_MOBILE_SLIDER } from 'mobile/constants/layout';
import { demoChannel } from 'shared/constants/demo-channel';

import 'shared/styles/core.styl';
import styles from './home.styl';


@decorate(WindowSize)
@connect(state => ({
    currentChannelId: state.currentChannelId,
    editMode: getEditMode(state),
    selectedVideoId: state.selectedVideoId,
    videoIdFromUrl: getVideoIdFromUrl(state),
    videos: getVideosGroupedByIds(state),
    videoIds: state.lazyVideoLists[MOBILE_WIDGET_DATA_KEY].videoIds,
    mainVideoId: getMainVideoId(state),
    listCategory: state.lazyVideoLists[MOBILE_WIDGET_DATA_KEY].category,
  }),
  dispatch => ({
    // it's because of we are using multireducer
    ...bindActionCreators({
      loadFirstPage,
      addToList,
    }, dispatch, MOBILE_WIDGET_DATA_KEY),
    ...bindActionCreators({
      selectVideo,
      fetchCurrentSiteUser,
      fetchAppSettings,
      getChannel,
      getPaymentInfo,
      getVideo,
    }, dispatch),
  })
)
export default class HomeLayout extends React.Component {

  static propTypes = {
    fetchCurrentSiteUser: PropTypes.func,
    fetchAppSettings: PropTypes.func,
    getChannel: PropTypes.func,
    getPaymentInfo: PropTypes.func,
    loadFirstPage: PropTypes.func,
    listCategory: PropTypes.string,
    currentChannelId: PropTypes.string,
    selectedVideoId: PropTypes.string,
    videoIdFromUrl: PropTypes.string,
    editMode: PropTypes.string,
    selectVideo: PropTypes.func,
    getVideo: PropTypes.func,
    addToList: PropTypes.func,
    videos: PropTypes.object,
    videoIds: PropTypes.array,
    mainVideoId: PropTypes.string,
    windowSize: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      isReady: false,
    };
  }

  componentDidMount() {
    this.loadInitialData();
  }

  componentWillUpdate(nextProps) {
    const { videoIdFromUrl, selectedVideoId, videos = {} } = nextProps;

    if (videoIdFromUrl && !selectedVideoId && !_.isEmpty(videos[videoIdFromUrl])) {
      this.props.selectVideo(videoIdFromUrl);
      WixSDK.navigateToCurrentComponent();
    }
  }

  loadInitialData() {
    const {
      fetchCurrentSiteUser,
      getPaymentInfo,
      getChannel,
    } = this.props;

    const uid = WixSDK.Utils.getUid();

    bi.logWidgetSystem('mobile.data.initLoad');

    this.props.fetchAppSettings().then(({ fonts }) => {
      const channelId = fonts.channelId || demoChannel.id;
      const singleVideoId = fonts.videoId;
      const currentSiteUserPromise = uid ? fetchCurrentSiteUser() : Promise.resolve();
      const paymentInfoPromise = uid ? getPaymentInfo(channelId) : Promise.resolve();
      const channelPromise = getChannel(channelId);
      const videosPromise = singleVideoId ?
        this.loadSingleVideo(channelId, singleVideoId) :
        this.loadInitialVideos();

      Promise.all([
          currentSiteUserPromise,
          paymentInfoPromise,
          channelPromise,
          videosPromise,
        ])
        .then(this.loadVideoFromUrl)
        .then(() => {
          bi.logWidgetSystem('mobile.data.loaded');
          this.setState({ isReady: true });
        });
    });
  }

  loadInitialVideos() {
    const { loadFirstPage, listCategory } = this.props;

    return loadFirstPage(VIDEOS_COUNT_MOBILE_SLIDER, { category: listCategory })
      .then(this.addMainVideoToList);
  }

  loadSingleVideo(channelId, singleVideoId) {
    const { getVideo, addToList, selectVideo } = this.props;

    return getVideo(channelId, singleVideoId)
      .then(() => addToList(singleVideoId))
      .then(() => selectVideo(singleVideoId));
  }

  addMainVideoToList = () => {
    const { videoIds, mainVideoId, addToList } = this.props;

    if (_.includes(videoIds, mainVideoId)) {
      return;
    }

    addToList(mainVideoId);
  };

  loadVideoFromUrl = () => {
    const { videoIdFromUrl, currentChannelId, videos = {}, getVideo, addToList } = this.props;

    if (!videoIdFromUrl || !_.isEmpty(videos[videoIdFromUrl])) {
      return Promise.resolve();
    }

    return getVideo(currentChannelId, videoIdFromUrl)
      .then(() => addToList(videoIdFromUrl));
  };

  render() {
    const { currentChannelId, editMode, windowSize } = this.props;

    if (!this.state.isReady || !windowSize.width) {
      return (
        <MobileSpinnerOverlay />
      );
    }

    return (
      <LocaleProvider>
        <div className={styles.container}
             data-view-mode={editMode}
             role="presentation">
          <WidgetHeightByContentSync windowSize={windowSize}>
            <HomeView windowSize={windowSize} />
          </WidgetHeightByContentSync>

          <BiHandler />
          <BIUnhandledErrorLogger />
          <PaymentsBiHandler />
          <UpdateChannelPublishedState channelId={currentChannelId} />
          <PaymentEventProxy />
          <ReloadChannelPaymentDataOnPayment />
          <ClearVideoUrlsCacheOnPayment />
          <MobileModal />
          <TooltipPopout />
        </div>
      </LocaleProvider>
    );
  }
}



// WEBPACK FOOTER //
// ./src/mobile/layouts/home/home.js